import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../ui/dialog";
import { api, errMsg } from "../../../lib/api";
import UserPicker from "./UserPicker";

const METHODS = [["upi", "UPI"], ["bank", "Bank transfer"], ["cash", "Cash"], ["card", "Card"], ["other", "Other"]];
const field = "w-full h-10 px-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-900 outline-none focus:border-sky-400 focus:bg-white";
const chip = (on) => `h-8 px-3 rounded-full text-xs transition-colors ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`;
const EMPTY = { credits: 50, inr: "", method: "upi", reference: "", note: "", paid: false };

export const AddCreditDialog = ({ open, user, onClose, onDone }) => {
  const [who, setWho] = useState(user || null);
  const [f, setF] = useState(EMPTY);
  const [unit, setUnit] = useState(0);
  const [panel, setPanel] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setWho(user || null); setF(EMPTY);
    api.creditsPricing().then((r) => setUnit(r.unit_price_inr)).catch(() => {});
    api.adminPanel().then((r) => setPanel(r.balance)).catch(() => {});
  }, [open, user]);
  const set = (u) => setF((x) => ({ ...x, ...u }));
  const suggested = Math.max(1, Math.ceil(Number(f.credits || 0) * unit));
  const inr = f.inr === "" ? suggested : Number(f.inr);

  const submit = async (e) => {
    e.preventDefault();
    if (!who) return toast.error("Pick the merchant first");
    if (f.paid && !(inr > 0)) return toast.error("Enter the amount received, or choose Free gift");
    setBusy(true);
    const body = f.paid ? { amount: Number(f.credits), amount_inr: inr, method: f.method, reference: f.reference || undefined, note: f.note || undefined }
      : { amount: Number(f.credits), note: f.note || undefined };
    try {
      await api.adminAddCredit(who.id, body);
      toast.success(`Added ${f.credits} credits to ${who.email}`, { description: f.paid ? `₹${inr.toLocaleString("en-IN")} payment recorded` : "Taken from your panel balance" }); window.dispatchEvent(new Event("emergent-balance-changed"));
      onDone?.(); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white border-slate-200" data-testid="add-credit-dialog">
        <DialogTitle className="text-slate-900 flex items-center gap-2"><Zap size={16} className="fill-amber-400 text-amber-500" /> Add credit</DialogTitle>
        <DialogDescription className="text-sm text-slate-500">Send credits from your panel to any user (your son, a friend, a merchant). They can use them right away.</DialogDescription>
        <form onSubmit={submit} className="space-y-4">
          <UserPicker value={who} onPick={setWho} />
          <div className="flex gap-2" data-testid="add-credit-type">
            <button type="button" onClick={() => set({ paid: true })} data-testid="add-credit-type-paid" className={chip(f.paid)}>They paid me</button>
            <button type="button" onClick={() => set({ paid: false })} data-testid="add-credit-type-free" className={chip(!f.paid)}>From my panel</button>
          </div>
          {panel !== null && <p className={`text-xs ${panel < Number(f.credits || 0) ? "text-rose-600" : "text-slate-500"}`} data-testid="add-credit-panel-balance">
            Panel balance: <b className="font-mono">{panel.toLocaleString("en-IN")}</b> credits — these credits are taken from it.</p>}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-500 space-y-1.5 block">Credits
              <input type="number" min={1} max={100000} required value={f.credits} onChange={(e) => set({ credits: e.target.value })} data-testid="add-credit-amount" className={`${field} font-mono`} /></label>
            {f.paid && <label className="text-xs text-slate-500 space-y-1.5 block">Amount received (₹)
              <input type="number" min={0} step="0.01" value={f.inr} placeholder={String(suggested)} onChange={(e) => set({ inr: e.target.value })} data-testid="add-credit-inr" className={`${field} font-mono`} /></label>}
          </div>
          {f.paid && (
            <>
              <div className="flex flex-wrap gap-1.5" data-testid="add-credit-methods">
                {METHODS.map(([id, l]) => <button type="button" key={id} onClick={() => set({ method: id })} data-testid={`add-credit-method-${id}`} className={chip(f.method === id)}>{l}</button>)}
              </div>
              <input value={f.reference} onChange={(e) => set({ reference: e.target.value })} maxLength={80} placeholder="Transaction / UTR number (optional)" data-testid="add-credit-reference" className={field} />
            </>
          )}
          <input value={f.note} onChange={(e) => set({ note: e.target.value })} maxLength={200} placeholder="Note (optional)" data-testid="add-credit-note" className={field} />
          {f.paid && <p className="text-[11px] text-slate-400">Your price for {f.credits || 0} credits is ₹{suggested.toLocaleString("en-IN")}. This payment is added to your earnings.</p>}
          <button type="submit" disabled={busy || !who || Number(f.credits) < 1} data-testid="add-credit-submit"
            className="w-full h-11 rounded-full bg-slate-900 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-700 disabled:opacity-50 transition-colors">
            {busy && <Loader2 size={15} className="animate-spin" />} Add {f.credits || 0} credits
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCreditDialog;
